import { useQueryClient } from '@tanstack/react-query';
import { postV1MessagingChannelsIdRead } from '@treadinc/horizon-api-spec';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

import { MessagingChannel, MessagingMessage } from '~hooks/useMessaging/models';
import { isPageVisible } from '~hooks/useMessaging/pageVisibility';
import { reReadUnreadCountsSoon } from '~hooks/useMessaging/reReadUnreadCountsSoon';
import useMessagingChannelCachePatch, {
  channelPatchVersion,
} from '~hooks/useMessaging/useMessagingChannelCachePatch';
import { Nullable } from '~types/Nullable';

const subscribeToVisibility = (onChange: () => void) => {
  document.addEventListener('visibilitychange', onChange);

  return () => document.removeEventListener('visibilitychange', onChange);
};

// A burst of messages into an open conversation is marked once, through the newest of them.
export const MARK_READ_DELAY_MS = 300;

// One more attempt for a mark that failed, since nothing else re-runs it while the conversation stays open.
export const MARK_READ_RETRY_DELAY_MS = 5000;

// A hidden tab is not reading, so marking waits until the page is visible again.
export default function useMarkChannelRead({
  channel,
  isRefreshing,
  messages,
}: {
  channel: Nullable<MessagingChannel>;
  isRefreshing: boolean;
  messages: MessagingMessage[];
}) {
  const queryClient = useQueryClient();
  const { patchChannelInCaches } = useMessagingChannelCachePatch();
  const isVisible = useSyncExternalStore(subscribeToVisibility, isPageVisible);
  const lastMarkedMessageId = useRef<string>();
  const retriedMessageId = useRef<string>();
  const retryTimer = useRef<ReturnType<typeof setTimeout>>();
  const [retryTick, setRetryTick] = useState(0);

  // Deleted messages never count as unread, and the api refuses to mark through one it withholds.
  const newestMessageId = messages.findLast((message) => !message.isDeleted)?.id;
  const channelId = channel?.id;
  const latestOpenConversation = useRef({ channelId, newestMessageId });
  latestOpenConversation.current = { channelId, newestMessageId };
  const unreadCount = channel?.unreadCount ?? 0;

  useEffect(() => {
    const shouldMarkRead =
      !!channelId &&
      !!newestMessageId &&
      unreadCount > 0 &&
      isVisible &&
      !isRefreshing &&
      lastMarkedMessageId.current !== newestMessageId;
    if (!shouldMarkRead) return;

    const markRead = async () => {
      lastMarkedMessageId.current = newestMessageId;
      const patchVersionBeforeMark = channelPatchVersion(queryClient, channelId);

      try {
        const response = await postV1MessagingChannelsIdRead({
          path: { id: channelId },
          body: { last_read_message_id: newestMessageId },
        });
        const markedChannel = MessagingChannel.parse(response.data.data);

        // A message or realtime event that landed while this was in flight is newer than the
        // response, and the next mark carries the count forward.
        const isNewerMessageLoaded =
          latestOpenConversation.current.channelId === channelId &&
          latestOpenConversation.current.newestMessageId !== newestMessageId;
        const isPatchedMeanwhile =
          channelPatchVersion(queryClient, channelId) !== patchVersionBeforeMark;
        if (!isNewerMessageLoaded && !isPatchedMeanwhile) {
          patchChannelInCaches(channelId, (cachedChannel) =>
            cachedChannel.withReadState({
              members: markedChannel.members,
              unreadCount: markedChannel.unreadCount,
            }),
          );
        }
        retriedMessageId.current = undefined;
        // A late unread event can put the count back after this mark, so the message stays markable.
        lastMarkedMessageId.current = undefined;
        if (isPatchedMeanwhile) setRetryTick((tick) => tick + 1);
        reReadUnreadCountsSoon(queryClient);
      } catch {
        // No toast for a background mark: the interceptor reports it to Sentry.
        lastMarkedMessageId.current = undefined;
        if (retriedMessageId.current !== newestMessageId) {
          retriedMessageId.current = newestMessageId;
          retryTimer.current = setTimeout(
            () => setRetryTick((tick) => tick + 1),
            MARK_READ_RETRY_DELAY_MS,
          );
        }
      }
    };

    const scheduledMark = setTimeout(() => void markRead(), MARK_READ_DELAY_MS);

    return () => clearTimeout(scheduledMark);
  }, [
    channelId,
    isRefreshing,
    isVisible,
    newestMessageId,
    patchChannelInCaches,
    queryClient,
    retryTick,
    unreadCount,
  ]);

  useEffect(() => () => clearTimeout(retryTimer.current), []);
}
